/**
 * Root Reducer
 *
 * Combines all feature reducers into a single root reducer.
 * Uses redux-persist to persist the 'login' slice to localStorage,
 * ensuring the user stays authenticated across page reloads.
 *
 * NOTE: All RootState properties are typed as 'any'. Consider defining
 * proper interfaces for each slice to improve type safety across the app.
 */
import { combineReducers, Reducer } from 'redux';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import dashboardReducer from './dashboard/dashboard.reducer';
import dashboardfordepartmentReducer from './dashboardfordepartment/dashboardfordepartment.reducer';
import addEmployeeReducer from './AddEmployee/AddEmployee.reducer';
import generalReducer from './general/general.reducer';
import employeelistReducer from './employeelist/employeelist.reducer';
import sectionWiseFetchDetailsReducer from './SectionWiseFetchDetails/SectionWiseFetchDetails.reducer';
import loginReducer from './login/login.reducer';
import branchWiseFetchDetailsReducer from './BranchWiseFetchDetails/BranchWiseFetchDetails.reducer';
import getValueDashboardForIndividualReducer from './GetValueDashboardForIndividual/GetValueDashboardForIndividual.reducer';
import reportsReducer from './Reports/Reports.reducer';
import refreshmentApprovalReducer from './RefreshmentApproval/RefreshmentApproval.reducer';
import newspaperApprovalReducer from './NewspaperApproval/NewspaperApproval.reducer';
import leaveReasonMasterReducer from './LeaveReasonMaster/LeaveReasonMaster.reducer';
import organisationReducer from './organisation/organisation.reducer';
import branchReducer from './branch/branch.reducer';
import sectionReducer from './section/section.redux';
import transferEmployeeDetailsReducer from './TransferEmployee/TransferEmployee.reducer';
import webCheckInReducer from './webCheckIn/webCheckIn.reducer';
import visitorPassReducer from './visitorPass/visitorPass.reducer';
import { leaveRestrictionReducer } from './leaveRestriction/leaveRestriction.reducer';
import monthlyCheckoutResponse from './MonthlyCheckout/MonthCheckout.reducer';
import compOffReportReducer from './CompOffReport/CompOffReport.reducer';
import newReportReducer from './NewReport/NewReport.reducer';
import applyLeaveAdminReducer from './ApplyLeaveAdmin/ApplyLeaveAdmin.reducer';
import leaveAdjustmentReducer from './LeaveAdjustment/LeaveAdjustment.reducer';
import resetPasswordReducer from './ResetPassword/ResetPassword.reducer';
import requestCompOffAdminReducer from './RequestCompOffAdmin/RequestCompOffAdmin.reducer';

// Persist configuration: only the 'login' slice is saved to localStorage
// This preserves JWT token and user data across browser refreshes
const persistConfig = {
	key: 'root',
	storage,
	whitelist: ['login'],
};

interface RootState {
	dashboard: any;
	login: any;
	general: any;
	dashboardfordepartment: any;
	addEmployee: any;
	employeelist: any;
	sectionWiseFetchDetails: any;
	branchWiseFetchDetails: any;
	getValueDashboardForIndividual: any;
	reports: any;
	refreshmentApproval: any;
	newspaperApproval: any;
	leaveReasonMaster: any;
	organisation: any;
	branch: any;
	section: any;
	transferEmployeeDetails: any;
	webCheckIn: any;
	visitorPass: any;
	leaveRestriction: any;
	monthlyCheckoutResponse: any;
	compOffReport: any;
	newReport: any;
	applyLeaveAdmin: any;
	leaveAdjustment: any;
	resetPassword: any;
	requestCompOffAdmin: any;
}

export type { RootState };

const rootReducer: Reducer<RootState> = combineReducers<RootState>({
	dashboard: dashboardReducer,
	login: loginReducer,
	dashboardfordepartment: dashboardfordepartmentReducer,
	general: generalReducer,
	addEmployee: addEmployeeReducer,
	employeelist: employeelistReducer,
	sectionWiseFetchDetails: sectionWiseFetchDetailsReducer,
	branchWiseFetchDetails: branchWiseFetchDetailsReducer,
	getValueDashboardForIndividual: getValueDashboardForIndividualReducer,
	reports: reportsReducer,
	refreshmentApproval: refreshmentApprovalReducer,
	newspaperApproval: newspaperApprovalReducer,
	leaveReasonMaster: leaveReasonMasterReducer,
	organisation: organisationReducer,
	branch: branchReducer,
	section: sectionReducer,
	transferEmployeeDetails: transferEmployeeDetailsReducer,
	webCheckIn: webCheckInReducer,
	visitorPass: visitorPassReducer,
	leaveRestriction: leaveRestrictionReducer,
	monthlyCheckoutResponse: monthlyCheckoutResponse,
	compOffReport: compOffReportReducer,
	newReport: newReportReducer,
	applyLeaveAdmin: applyLeaveAdminReducer,
	leaveAdjustment: leaveAdjustmentReducer,
	resetPassword: resetPasswordReducer,
	requestCompOffAdmin: requestCompOffAdminReducer,
});

const persistedReducer = persistReducer<RootState>(persistConfig, rootReducer);

export default persistedReducer;
